import { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, SvgIcon, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { useLocation, useNavigate } from 'react-router-dom';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import CategoriesTab from './components/CategoriesTab';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const CategoriesPage = () => {
  const { t } = useTranslation('categories');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultFiltersLoading } = useInstanceMarket();
  const [hasPermissionToCreateCategory, setHasPermissionToCreateCategory] = useState<boolean>(false);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  
  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');
  useEffect(() => {
    setHasPermissionToCreateCategory(hasPermissionTo(['CategoryCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');

  }, [hasPermissionTo, location.search]);

  const handleCreateNew = () => {
    navigate('/procurement/categories/create');
  };

  const canViewCategories = hasPermissionTo(['CategoryView', 'ConfigurationAdmin']);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !defaultFiltersLoading && defaultInstanceId) {
      setCurrentFilterInstance(defaultInstanceId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId]);

  const handleFilterChange = (instance: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (!canViewCategories) {
    return null;
  }

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/settings')}
        title={t('backToSettings', 'Back to Settings')}
        isLink={false}
        styles={{ marginBottom: '20px' }} />
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 4,
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle>{t('procurementPageTitle', 'Procurement Categories')}</PageTitle>
        </Box>

        {hasPermissionToCreateCategory && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('procurementCreateNew', 'Create New Procurement Category')}
            </Button>
          </Box>
        )}
      </Stack>
      
      <CategoriesTab 
        onCreateNew={handleCreateNew} 
        onFilterChange={handleFilterChange}
        initialInstance={currentFilterInstance}
        initialSearchTerm={currentFilterSearchTerm}
      />
    </PageArea>
  );
};

export default CategoriesPage;
